import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useContext, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../Context/UserContextAPI";
import { commonUserListColumns } from "../../../components/Table/CommonColumnsForUserList";
import UserSearchForm from "../../../components/Users/UserSearchForm";
import { calculateUserMedicalStatus } from "../../../components/Users/calculateUserMedicalStatus";
import useIsOneIntroducer from "../../../hooks/useIsOneIntroducer";
import Loading from "../../../utils/Loader/Loading";
import request from "../../../utils/request";
import UpdateMofaModal from "./UpdateMofaModal";
import { FaTrash } from "react-icons/fa";
import { Button } from "react-bootstrap";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";

const MofaList = () => {
  const { accessPerm } = useContext(UserContext);
  const [mofaModalOpen, setMofaModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({
    fromDate: new Date(),
    toDate: new Date(),
  });

  const [selectedModal, setSelectedModal] = useState({
    name: "",
    selectedId: "",
  });

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  // Get Specific date users-------
  const { data: usersData, isFetching: loading } = useQuery({
    queryKey: ["users", "MofaList", filterData],
    queryFn: async () => {
      const query = new URLSearchParams(filterData).toString();
      const response = await request.getAll(
        `v2/users/search-mofa-users?${query}`,
      );

      if (response.status !== "OK") {
        toast.error("There is a server side error.");
      }

      return response.data || [];
    },
    placeholderData: () => [],
  });

  // custom hooks
  const isOneIntroducer = useIsOneIntroducer();

  const columnHelper = createColumnHelper();

  const commonColumns = useMemo(() => {
    // Move the column filtering logic to a separate function for clarity
    const filterColumns = (columns) =>
      columns.filter(
        (item) =>
          ((isOneIntroducer ? item.id !== "agency" : true) &&
            item.id === "SL.2") ||
          item.id === "name" ||
          item.id === "passport_no" ||
          item.id === "mofa" ||
          item.id === "mofa_updated_at",
      );

    // Apply the filter to the common columns
    return filterColumns(commonUserListColumns(usersData));
  }, [usersData]);

  const columns = useMemo(
    () => [
      ...commonColumns,

      columnHelper.accessor("mofa", {
        id: "mofa",
        header: () => (
          <span className="text-center d-flex justify-content-center">
            MOFA Number
          </span>
        ),
        cell: (info) => {
          return (
            <div className="d-flex justify-content-start">
              {info?.row?.original?.mofa_no || ""}
            </div>
          );
        },
      }),
      columnHelper.accessor("mofa_updated_at", {
        id: "mofa_updated_at",
        header: () => (
          <span className="text-center d-flex justify-content-center">
            MOFA Upload Date
          </span>
        ),
        cell: (info) => {
          const formattedDate = format(
            new Date(info?.row?.original?.mofa_updated_at),
            "dd-MM-yyyy",
          );
          return (
            <div className="d-flex justify-content-start">
              {(info?.row?.original?.mofa_updated_at && formattedDate) || ""}
            </div>
          );
        },
      }),
      columnHelper.accessor("mofa_updated_at", {
        id: "mofa_updated_at",
        header: () => (
          <span className="text-center d-flex justify-content-center">
            Action
          </span>
        ),
        cell: (info) => {
          const userId = info?.row?.original?.id;

          return (
            <div className="d-flex justify-content-start">
              <Button
                variant="danger"
                onClick={() => {
                  setSelectedModal({ name: "delete", selectedId: userId });
                }}
              >
                <FaTrash size={20} />
              </Button>
            </div>
          );
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessPerm, usersData],
  );

  const table = useReactTable({
    data: usersData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRemoveMofa = async () => {
    try {
      const res = await request.delete(
        "v2/users/remove-mofa-information",
        selectedModal.selectedId,
      );
      if (res.status === "OK") {
        // toast.success("Mofa removed successfully");
        setSelectedModal({ name: "", selectedId: "" });
        queryClient.invalidateQueries({ queryKey: ["users", "MofaList"] });
      } else {
        toast.error("There is a server side error.");
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <div className="card patient-information patient-container">
        <div className="card-body">
          <div className="rounded">
            <div className="card shadow-none mb-3">
              <div className="border p-3 rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <h6
                    className="mb-0 btn btn-danger text-white text-capitalize fw-bold p-0 m-0"
                    style={{ cursor: "default" }}
                  >
                    Mofa list
                  </h6>
                  {accessPerm(32, 1) && (
                    <button
                      onClick={(e) => {
                        setMofaModalOpen(true);
                      }}
                      type="button"
                      className="btn btn-primary border border-3"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <i className="fa-solid fa-plus"></i> Update Mofa
                    </button>
                  )}
                </div>
                <UserSearchForm
                  userSearchHandler={(data) => {
                    setFilterData({ ...filterData, ...data });
                  }}
                  inlineButtonComponent={
                    <button
                      type="button"
                      className="btn btn-info"
                      style={{ whiteSpace: "nowrap" }}
                      onClick={() => {
                        const params = new URLSearchParams({
                          fromDate: filterData.fromDate,
                          toDate: filterData.toDate,
                        });

                        window.open(
                          `/mofa-list/print?${params.toString()}`,
                          "_blank",
                        );
                      }}
                    >
                      Print
                    </button>
                  }
                />
              </div>
            </div>

            {loading ? (
              <Loading />
            ) : (
              <table className="table align-middle table-hover table-bordered mb-0 rounded">
                <thead className="bg-primary text-white">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup?.id}>
                      {headerGroup?.headers?.map((header) => (
                        <th
                          key={header?.id}
                          className={`p-1`}
                          style={{ backgroundColor: "#095d7e" }}
                        >
                          {header?.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row, i) => {
                    const { isDone, status } = calculateUserMedicalStatus(
                      row?.original,
                    );

                    const rowClass = (() => {
                      if (status === 2) {
                        return isDone ? "text-success" : "";
                      } else if (status === 3) {
                        return isDone ? "text-danger" : "";
                      } else if (status === 4) {
                        return isDone ? "text-primary" : "";
                      } else {
                        return "text-dark";
                      }
                    })();
                    return (
                      <tr key={i} className={rowClass}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="p-1">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <UpdateMofaModal
        isOpen={mofaModalOpen}
        onClose={() => setMofaModalOpen(false)}
      />

      <DeleteModal
        isOpen={selectedModal.name === "delete"}
        onClose={() => {
          setSelectedModal({
            name: "",
            selectedId: "",
          });
        }}
        handleDelete={handleRemoveMofa}
      />
    </>
  );
};

export default MofaList;
